import { getSalesOrdersTemplate } from "@/api/template";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import HelpButton from "@/components/HelpButton/HelpButton";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import Table, { Column, TableProps } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import { IOrder, useOrders, useOrderStatusPerYear } from "@/hooks/useOrders";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import { DeleteOutlined, DownloadOutlined, ExportOutlined, LinkOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useState } from "react";
import { useDashboardYear } from "../dashboard/hooks/useDateRange";
import DeleteOrdersAction from "../purchase_orders/DeleteOrdersAction";
import ExportOrdersAction from "../purchase_orders/ExportOrdersAction";
import { SALES_ORDER_UPLOAD_COLUMNS } from "./constants";

const columns: Column<IOrder>[] = [
  {
    label: "Order ID",
    key: "order_id",
    searchable: true,
    minWidth: "140px",
  },
  {
    label: "Order date",
    key: "order_date",
    sortable: true,
    minWidth: "150px",
  },
  {
    label: "Product ID",
    key: "product_external_id",
    minWidth: "160px",
    searchable: true,
    renderer: (record) => (
      <CrossReference to={"/products/" + record.product_id}>
        {record.product_external_id}
      </CrossReference>
    ),
  },
  {
    label: "Product Name",
    key: "product_name",
    minWidth: "180px",
    searchable: true,
    maxWidth: "2fr",
    renderer: (record) => (
      <CrossReference to={"/products/" + record.product_id}>{record.product_name}</CrossReference>
    ),
  },
  {
    label: "Quantity",
    key: "quantity",
    minWidth: "140px",
    sortable: true,
  },
  {
    key: "departure_facility_name",
    label: "Departure facility",
    searchable: true,
    maxWidth: "2fr",
    minWidth: "230px",
    sortable: true,
    renderer: (record) => (
      <CrossReference to={"/facilities/" + record.departure_facility_id}>
        {record.departure_facility_name}
      </CrossReference>
    ),
  },
  {
    key: "arrival_facility_name",
    label: "Arrival facility",
    minWidth: "210px",
    searchable: true,
    maxWidth: "2fr",
    sortable: true,
    renderer: (record) =>
      record.arrival_facility_id ? (
        <CrossReference to={"/facilities/" + record.arrival_facility_id}>
          {record.arrival_facility_name}
        </CrossReference>
      ) : (
        <>-</>
      ),
  },
  {
    key: "arrival_name",
    label: "Arrival name",
    searchable: true,
    maxWidth: "2fr",
    minWidth: "190px",
    sortable: true,
    renderer: (record) => record.arrival_name || "-",
  },
  {
    key: "arrival_address",
    label: "Arrival address",
    minWidth: "210px",
    searchable: true,
    maxWidth: "2fr",
    sortable: true,
    renderer: (record) => record.arrival_address || "-",
  },
  {
    key: "transport_category",
    label: "Transport category",
    searchable: true,
    minWidth: "240px",
    sortable: true,
    renderer: (record) => record.transport_category || "-",
  },
  {
    label: "Status",
    key: "status",
    minWidth: "130px",
    filterable: true,
    renderer: (po) => {
      return <StatusTag status={po.status} />;
    },
  },
  {
    label: "Actions",
    key: "actions",
    minWidth: "85px",
    maxWidth: "85px",
    renderer: (record) => {
      return (
        <MoreButton
          buttonProps={{ type: "text" }}
          actions={
            <>
              <ActionsPopOverItem
                to={`/transport/outbound/${record.departure_facility_id}/${
                  record.arrival_facility_id || record.arrival_name
                }?search_order_id=${record.order_id}`}
                icon={<LinkOutlined />}
              >
                View transport
              </ActionsPopOverItem>
              <ActionsPopOverItem
                to={`/transport/outbound/${record.departure_facility_id}/${record.transport_category}?by_category=true&search_order_id=${record.order_id}`}
                icon={<LinkOutlined />}
              >
                View transport (by category)
              </ActionsPopOverItem>
            </>
          }
        />
      );
    },
  },
];

export default function SalesOrders() {
  useTrackOnLoad("Sales Orders Viewed");

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
    });

  const { year } = useDashboardYear();

  const { data: orderYears, isLoading: orderYearsLoading } = useOrderStatusPerYear({
    orderType: "sales",
  });

  const { data, isLoading, isFetching } = useOrders({
    orderType: "sales",
    pagination,
    filter,
    search,
    sort,
    year,
    enabled: !!year,
  });

  const availableFilters = {
    status: [
      { label: "Complete", value: "complete" },
      { label: "Incomplete", value: "incomplete" },
      { label: "Error", value: "errors" },
    ],
  };

  const tableProps: TableProps<IOrder> = {
    columns: columns,
    availableFilters,
    rowKey: "id",
    onSearchFilterSortPaginate,
    loading: isLoading || isFetching,
    data: data?.data,
    totalRows: data?.total_count,
    pagination,
    sort,
    filter,
    search,
  };

  const changeYear = () => {
    setPage(1);
  };

  const [isMoreOpen, setIsMoreOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title="Sales orders"
        description="Start with uploading all relevant sales orders."
        right={
          <>
            <HelpButton
              size="small"
              type="link"
              url="https://help.root-sustainability.com/en/articles/9898467-sales-orders"
            />
            <PageButtonGroup>
              <FileUploader
                uploadType={"sale_orders"}
                uploadDataTableProps={{
                  columns: SALES_ORDER_UPLOAD_COLUMNS,
                  rowKey: "id",
                  availableFilters,
                }}
                templateUrlFn={getSalesOrdersTemplate}
              />
              <FileDownloader
                fileOutputName="template-sales-orders.xlsx"
                fileUrlFn={getSalesOrdersTemplate}
                buttonText={"Template"}
                icon={<DownloadOutlined />}
              />
            </PageButtonGroup>
          </>
        }
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                onChange={changeYear}
                optionData={orderYears}
                isLoading={orderYearsLoading}
              />
              <PageButtonGroup loading={orderYearsLoading}>
                <ExportOrdersAction
                  orderType={"sales"}
                  year={year}
                >
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportOrdersAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  actions={
                    <DeleteOrdersAction
                      orderType={"sales"}
                      year={year}
                      onSuccess={() => setIsMoreOpen(false)}
                    >
                      <ActionsPopOverItem
                        icon={<DeleteOutlined />}
                        danger
                      >
                        Delete
                      </ActionsPopOverItem>
                    </DeleteOrdersAction>
                  }
                />
              </PageButtonGroup>
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
            />
          }
        />
        <Table {...tableProps} />
      </div>
    </div>
  );
}
